import type { JSONContent } from '@tiptap/react';

/**
 * Headings the notes generator emits, lowercased. Titles are `z.literal`-constrained
 * in packages/core/src/ai/schemas.ts, so this is a closed set; matching on prefixes
 * covers the day-of-week variants ("... (since Friday)", "Group Blocks Added Yesterday"
 * vs "... Since Friday") without listing each one.
 */
const GENERATED_SECTION_PREFIXES = [
  'last night performance summary',
  'key metrics',
  '5-day lowest rate outlook',
  'rate recommendations',
  'groups remaining in the next',
  'group blocks added',
  'additional notes',
];

const nodeText = (node: JSONContent): string => {
  if (typeof node.text === 'string') return node.text;
  if (!Array.isArray(node.content)) return '';
  return node.content.map(nodeText).join('');
};

const isGeneratedHeading = (node: JSONContent): boolean => {
  if (node.type !== 'heading') return false;
  const text = nodeText(node).trim().toLowerCase();
  return GENERATED_SECTION_PREFIXES.some((prefix) => text.startsWith(prefix));
};

const blockLines = (node: JSONContent): string[] => {
  if (node.type === 'bulletList' || node.type === 'orderedList') {
    return (node.content ?? []).map((item) => nodeText(item).trim()).filter(Boolean);
  }
  const text = nodeText(node).trim();
  return text ? [text] : [];
};

/**
 * Returns only the user-authored text from the notes editor, dropping every section
 * the generator produced.
 *
 * Regenerating notes sends the editor's current contents back as `existingNotes`, and
 * the LLM files those into "Additional Notes". Without this filter the previous
 * generation is swallowed into Additional Notes on every run, so the same Key Metrics
 * and rate lines pile up a layer deeper each time (RM-2324).
 *
 * A generated heading starts a skipped region that ends at the next heading, so user
 * notes typed underneath their own heading survive.
 */
export function stripGeneratedSections(doc?: JSONContent | null): string {
  if (!doc || !Array.isArray(doc.content)) return '';

  const kept: string[] = [];
  let skipping = false;

  for (const node of doc.content) {
    if (node.type === 'heading') {
      skipping = isGeneratedHeading(node);
      if (skipping) continue;
    }
    if (skipping) continue;
    kept.push(...blockLines(node));
  }

  return kept.join('\n').trim();
}
